import { createAsyncThunk } from "@reduxjs/toolkit";

import { type ChatMessage, type UpdateMessageEvent } from "@fitness-app/data-models/entities/Chat";
import { type Database } from "@fitness-app/supabase";

import { getLoggedUser } from "../../../helpers/getLoggedUser";
import { type AsyncThunkCreator } from "../../../index";
import { isAssignedToChannel } from "../helpers/channelAccess";
import { isUnsentMessage } from "../helpers/unsentMessages";
import { CHANNEL_ACCESS_DENIED_ERROR, CHAT_REDUCER_NAME } from "../types";

type Payload = {
  content?: ChatMessage["content"];
  reactions?: ChatMessage["reactions"];
  /** Written whole (jsonb). Author only, like `content` — used to remove a link card. */
  extra?: ChatMessage["extra"];
  id: string;
  authorId: string;
  channelId: string;
  deleted?: true;
};

type ChatMessageUpdate = Database["public"]["Tables"]["chat_message"]["Update"];

export const updateChatMessage = createAsyncThunk<ChatMessage, Payload, AsyncThunkCreator<string>>(
  `${CHAT_REDUCER_NAME}/updateChatMessage`,
  async (payload, { rejectWithValue, getState, extra: { db, auth, parties, analytics, errorTrackingService } }) => {
    const { id } = await getLoggedUser(auth);

    if (!isAssignedToChannel(getState().chat, payload.channelId, id)) {
      return rejectWithValue(CHANNEL_ACCESS_DENIED_ERROR);
    }

    const message: Partial<ChatMessage> & { id: string } = {
      id: payload.id,
      updatedAt: new Date().toISOString(),
      ...(payload.content && payload.authorId === id ? { content: payload.content } : {}),
      ...(payload.reactions ? { reactions: payload.reactions } : {}),
      ...(payload.extra && payload.authorId === id ? { extra: payload.extra } : {}),
      ...(payload.deleted ? { deleted: payload.deleted } : {}),
    };

    const socket = parties.chatRooms[payload.channelId];
    const socketNative = parties.chatRoomsNative[payload.channelId];

    const { error, data } = await db
      .from("chat_message")
      .update(message as unknown as ChatMessageUpdate)
      .eq("id", payload.id)
      .select("*")
      .maybeSingle<ChatMessage>();

    if (error) {
      errorTrackingService?.recordError(error, "updateChatMessage");
      return rejectWithValue(error.message);
    }

    if (!data) {
      // RLS matched no row — the user was unassigned from the channel or the message is gone.
      // Not an application error, so it stays out of the error tracker.
      return rejectWithValue(CHANNEL_ACCESS_DENIED_ERROR);
    }

    analytics.track(
      payload.deleted ? "delete_chat_message" : payload.extra ? "remove_chat_link_preview" : "update_chat_message",
    );

    if (socket) {
      const socketMessage: UpdateMessageEvent = {
        type: "message",
        operation: "update",
        senderId: id,
        data: { ...message, extra: { ...message.extra, version: 2 } },
      };
      socket.send(JSON.stringify(socketMessage));
    }

    if (socketNative) {
      const socketMessage: UpdateMessageEvent = {
        type: "message",
        operation: "update",
        senderId: id,
        data: { ...message, extra: { ...message.extra, version: 2 } },
      };
      socketNative.send(JSON.stringify(socketMessage));
    }

    if (payload.deleted) {
      const activeChannel = getState().chat.activeChannels.find((channel) => channel.id === payload.channelId);

      if (activeChannel?.lastMessageId === payload.id) {
        // An unsent message (still sending, or failed and kept in the outbox) has no saved row:
        // pointing `lastMessageId` at it breaks the foreign key and the whole update fails.
        const newLastMessage = getState().chat.messages[payload.channelId]?.find(
          (item) => item.id !== payload.id && !item.deleted && !isUnsentMessage(item),
        );
        const { error } = await db
          .from("chat_channel")
          .update({ lastMessageId: newLastMessage?.id || null, updatedAt: new Date().toISOString() })
          .eq("id", payload.channelId);

        // The message itself is already deleted, so this is not reported as a failed delete.
        if (error) {
          errorTrackingService?.recordError(error, "updateChatMessage:lastMessageId");
        }
      }
    }

    return data;
  },
);
